/**
 * Companies Dashboard Component
 * 
 * Uses the generic DataDashboard component to display companies data.
 * This is a thin wrapper that handles company-specific logic.
 * 
 * Usage:
 * <CompaniesDashboard
 *   user={adminUser}
 *   currentRoute="/dashboard/customers"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 * />
 */

import React, { useState, useEffect } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import AddCompanyModal from '../../modals/AddCompanyModal';
import { AdminUser } from '../../../../types/admin-v2';
import { Customer } from '../../../../types/customer';
// import { companyColumns } from '../../../../config/data-dashboard-columns'; // File doesn't exist
import { API_ENDPOINTS } from '../../../../utils/config';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';

// Extended Customer interface with MongoDB _id and company_id
interface ExtendedCustomer extends Customer {
  _id?: string; // MongoDB _id for PATCH requests
  company_id?: string; // Company ID (C1, C2, C3...) for reference
}

interface CompaniesDashboardProps {
  /** Current admin user */
  user: AdminUser;
  /** Current route */
  currentRoute: string;
  /** Navigation handler */
  onNavigate: (route: string) => void;
  /** Logout handler */
  onLogout: () => void;
  /** Custom className */
  className?: string;
}

// Define company columns (only real database fields, no hardcoded values)
const companyColumns = [
  { key: 'global_id', title: 'Company ID', type: 'text' as const, sortable: true, editable: false },
  { key: 'name', title: 'Company Name', type: 'text' as const, sortable: true, editable: true },
  { key: 'email', title: 'Email', type: 'text' as const, sortable: true, editable: true },
  { key: 'phone', title: 'Phone', type: 'text' as const, sortable: true, editable: true },
  { key: 'country', title: 'Country', type: 'text' as const, sortable: true, editable: true },
  { key: 'city', title: 'City', type: 'text' as const, sortable: true, editable: true },
  { key: 'postal_code', title: 'Postal Code', type: 'text' as const, sortable: true, editable: true },
  { key: 'website', title: 'Website', type: 'text' as const, sortable: true, editable: true },
  {
    key: 'plan',
    title: 'Plan',
    type: 'dropdown' as const,
    sortable: true,
    editable: true,
    options: ['basic', 'plus', 'pro']
  },
  {
    key: 'status',
    title: 'Status',
    type: 'dropdown' as const,
    sortable: true,
    editable: true,
    options: ['active', 'inactive', 'pending', 'suspended']
  },
  { key: 'start_date', title: 'Created Date', type: 'date' as const, sortable: true, editable: false }
];

const CompaniesDashboard: React.FC<CompaniesDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  className = ''
}) => {
  const [companies, setCompanies] = useState<ExtendedCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 10, // Default to 10 for testing
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Load companies data
  useEffect(() => {
    loadCompanies();
  }, []);

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle open company (navigate to company users page)
  const handleOpenCompany = (companyId: string) => {
    onNavigate(`/dashboard/companies/${companyId}/users`);
  };


  const loadCompanies = async (page = 1, limit = 10, search = '', sortBy = 'company_id', sortOrder = 'asc', columnFilters: Record<string, string> = {}) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (search) {
        params.append('search', search);
      }

      // Add column filters to query parameters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value.trim()) {
          // Map frontend field names to backend field names
          let backendField = key;
          if (key === 'global_id') {
            backendField = 'company_id';
          }
          params.append(`filter_${backendField}`, value.trim());
        }
      });

      const response = await fetch(`${API_ENDPOINTS.COMPANIES}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to fetch companies');
      }

      const companiesData = responseData.data || [];
      const paginationInfo = responseData.pagination || {};


      // Transform backend data to ExtendedCustomer format (only real database fields)
      const transformedCompanies: ExtendedCustomer[] = companiesData.map((company: any, index: number) => ({
        id: company._id, // Use MongoDB _id for selection, deletion, and editing
        global_id: company.company_id || `C${String(index + 1).padStart(3, '0')}`,
        _id: company._id, // Keep MongoDB _id for reference
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        country: company.country || '',
        city: company.city || '',
        postal_code: company.postal_code || '',
        website: company.website || '',
        plan: company.plan || 'basic', // Plan field with default 'basic'
        status: (company.status || '').toLowerCase() as any, // Will be empty if not in database
        start_date: company.created_at ? new Date(company.created_at).toISOString().split('T')[0] : '',
        created_at: company.created_at,
        updated_at: company.updated_at,
        // Backend fields for reference
        company_id: company.company_id,
        // Fill in required Customer fields
        location: `${company.city || ''}, ${company.country || ''}`,
        account_manager: ''
        // Note: user_code, admin_code, procurement_code are excluded from display
      }));

      setCompanies(transformedCompanies);
      setPaginationInfo(paginationInfo);

    } catch (error) {
      console.error('Error loading companies:', error);
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle data change (for inline editing)
  const handleDataChange = (newData: any[]) => {
    setCompanies(newData);
  };


  // Handle backend data fetching with pagination, search, sort
  const handleDataFetch = (params: { page: number; limit: number; search?: string; sortBy?: string; sortOrder?: string; columnFilters?: Record<string, string> }) => {
    loadCompanies(params.page, params.limit || 10, params.search || '', params.sortBy || 'company_id', params.sortOrder || 'asc', params.columnFilters || {});
  };

  // Handle edit company
  const handleEditCompany = async (id: string, field: string, value: any) => {
    try {
      // First update frontend state optimistically
      setCompanies(prev => prev.map(company =>
        company.id === id
          ? { ...company, [field]: value }
          : company
      ));

      // Make API call to update company in backend (id is now _id)
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.COMPANIES}/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update error:', errorText);
        throw new Error(`Update failed: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();

    } catch (error) {
      console.error('Update error:', error);

      // Revert frontend state on error
      setCompanies(prev => prev.map(company =>
        company.id === id
          ? { ...company, [field]: companies.find(c => c.id === id)?.[field as keyof ExtendedCustomer] }
          : company
      ));

      // Show error to user (you can implement a toast notification here)
      alert(`Failed to update company: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle delete companies
  const handleDeleteCompanies = async (ids: string[]) => {
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(API_ENDPOINTS.COMPANIES, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_ids: ids
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Delete failed: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();

      if (responseData.success) {
        // Remove deleted companies from frontend state
        setCompanies(prev => prev.filter(company => !ids.includes(company.id)));

        // Show success message
        alert(`${responseData.deleted_count} companies deleted successfully`);

        // Refresh the data to get updated pagination info
        const currentPage = paginationInfo.page;
        const currentLimit = paginationInfo.limit;
        const currentSearch = '';
        const currentSortBy = 'company_id';
        const currentSortOrder = 'asc';

        loadCompanies(currentPage, currentLimit, currentSearch, currentSortBy, currentSortOrder);
      } else {
        throw new Error(responseData.message || 'Delete failed');
      }

    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete companies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportData)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('Export error:', errorText);
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }

      // Handle file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const baseFilename = exportData.filename || 'companies';
      a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  };

  return (
    <div className={`companies-dashboard ${className}`}>
      <DataDashboard
        title="Customers"
        collection="companies"
        data={companies}
        columns={companyColumns}
        isLoading={isLoading}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={handleDataChange}
        onEdit={handleEditCompany}
        onEditToggle={handleEditToggle}
        isEditing={isEditing}
        onDelete={handleDeleteCompanies}
        onExport={handleExport}
        onDataFetch={handleDataFetch}
        onOpen={handleOpenCompany}
        paginationInfo={paginationInfo}
        AddModal={AddCompanyModal}
        showAddButton={true}
        showSearch={true}
        showSelection={true}
        showEditButton={true}
        showDeleteButton={true}
        pageDescription="Manage customer information and user codes"
        getDynamicSubItems={getDynamicSubItems}
      />
    </div>
  );
};

export default CompaniesDashboard;
